import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import DropdownButton from "react-bootstrap/DropdownButton";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import { Product, UpdateProduct } from "../../../modals/Product";
import {
    SavedProductService,
    UpdateSavedProductService,
    removeItemFromSavedProductService,
} from "../../../services/services";
import {
    generalMessages,
    savedProductTypes,
    productFilters,
} from "../../../utils/constants";
import Loader from "../../Loader";
import SavedProduct from "./savedProduct";
import SavedProductDetail from "./savedProductDetail";
import AppContext from "../../../context/AppContext";
import SearchIcon from "../../../assets/images/Search_Icon.png";
import InfiniteScroll from 'react-infinite-scroll-component';
import { debounce } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';

const SavedProducts = () => {
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();

    const { tokenExpired } = useContext(AppContext);
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedProductFilter, setSelectedProductFilter] = useState(1);
    const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [page, setPage] = useState<number>(1);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortOption, setSortOption] = useState('');

    useEffect(() => {
        if (id) {
            setSelectedProduct(parseInt(id));
        } else {
            setSelectedProduct(null);

            getUserSavedProducts(selectedProductFilter, page, searchTerm, sortOption);
        }
    }, [id]); // eslint-disable-line

    const scrollPositionRef = useRef(0);

    // Before updating products, save scroll position
    const saveScrollPosition = () => {
        scrollPositionRef.current = window.pageYOffset;
    };

    // Restore scroll position after update
    const restoreScrollPosition = () => {
        window.scrollTo(0, scrollPositionRef.current);
    };

    const getUserSavedProducts = async (flag: number, page: number, search: string = '', sort: string = '') => {
        if (page > 1) {
            saveScrollPosition(); // Save position before loading more
        }

        setStatus(null);
        setLoading(true);
        setSelectedProductFilter(flag);
        SavedProductService(flag, page, search, sort)
            .then((result) => {
                if (result.data.data.length > 0) {
                    const newProducts = result.data.data;
                    if(page == 1){
                        setProducts(newProducts);
                    } else {
                        setProducts((prev) => [...prev, ...newProducts]);
                    }
                    setHasMore((page * 10) < result.data.total);
                } else {
                    setProducts([]);
                    setHasMore(false);
                }
                setLoading(false);
                // Restore scroll position for pagination
                if (page > 1) {
                    setTimeout(() => restoreScrollPosition(), 0);
                }
            })
            .catch((err) => {
                setLoading(false);
                if (err.response.status === 401) {
                    tokenExpired();
                } else {
                    setStatus(generalMessages.ERROR_MSG);
                }
            });
    };

    const filterProducts = (value: any) => {
        let sortOption = {
            "Ascending": "ascending", 
            "Descending": "descending",
            "Newest": "newest",
            "low to high": 'low_to_high',
            "high to low": 'high_to_low',
        }

        setSortOption(sortOption[value]);
        setProducts([]);
        setPage(1);
        setHasMore(true);
        getUserSavedProducts(selectedProductFilter, 1, searchTerm, sortOption[value]);
    };

    const handleMoveProduct = (id: number) => {
        const product = products.find((product) => product.id === id);
        if (product) {
            const newType = savedProductTypes.find(
                (t) => t.type !== product.type
            )?.value;
            const payload: UpdateProduct = {
                product_id: product.product_id,
                type: newType,
            };
            UpdateSavedProductService(id, payload)
                .then((result) => {
                    if (result.data.flag) {
                        setProducts([]);
                        setPage(1);
                        setHasMore(true);
                        getUserSavedProducts(selectedProductFilter, 1, searchTerm, sortOption);
                    }
                })
                .catch((err) => {
                    if (err.response.status === 401) {
                        tokenExpired();
                    }
                });
        }
    };

    const handleDeleteProduct = (id: number) => {
        removeItemFromSavedProductService(id)
            .then((result) => {
                if (result.data.flag) {
                    setProducts([]);
                    setPage(1);
                    setHasMore(true);
                    getUserSavedProducts(selectedProductFilter, 1, searchTerm, sortOption);
                }
            })
            .catch((err) => {
                if (err.response.status === 401) {
                    tokenExpired();
                }
            });
    };

    // Search feature
    const handleSearch = useMemo(
        () => debounce((value: string) => {
            setSearchTerm(value);
            setProducts([]);
            setPage(1);
            setHasMore(true);
            getUserSavedProducts(selectedProductFilter, 1, value, sortOption);
        }, 400),
        [selectedProductFilter, sortOption] // Recreate when these values change
    );
    
    const onSearchInputChange = (value) => {
        setSearchTerm(value);
        if(value.length >= 3 || value.length == 0){
            handleSearch(value);
        }
    };

    // Modified loadItem function to save state before navigation
    const handleLoadItem = (productId: number) => {
        setSelectedProduct(productId);

        history.push(`/product/saved/${productId}`); // unique URL for product detail
    };

    const handleBackToList = () => {
        setSelectedProduct(null);

        history.push(`/product/saved`);
    }

    return (selectedProduct && id) ? (
        <SavedProductDetail
            id={selectedProduct}
            backToList={handleBackToList}
        />
    ) : (
        <div className="product-wrapper  container-xl">
            <div
                className="form-group"
                style={{
                    position: "relative",
                    width: "-webkit-fill-available",
                }}
            >
                <input
                    style={{
                        width: "-webkit-fill-available",
                        // background: "#f4f4f4",
                        marginBottom: "20px",
                    }}
                    value={searchTerm}
                    onChange={(e) => onSearchInputChange(e.target.value)}
                    placeholder={"Search Saved Product"}
                    className="form-control"
                />

                <img
                    src={SearchIcon}
                    alt=""
                    style={{
                        position: "absolute",
                        right: 18,
                        bottom: 10,
                        height: 30,
                    }}
                />
            </div>

            {/* Loop Start */}
            <section className="product-details">
                <div className="container-xl">
                    <div className="sorting-block">
                        <Form.Group>
                            <Form.Control
                                onChange={(e) =>{
                                    const selected = +e.target.value;
                                    setSelectedProductFilter(selected);
                                    setPage(1);                        
                                    setHasMore(true);                  
                                    setProducts([]);                   
                                    getUserSavedProducts(selected, 1, searchTerm); 
                                }}
                                as="select"
                                value={selectedProductFilter}
                            >
                                {savedProductTypes.map((type, index) => (
                                    <option key={index} value={type.value}>
                                        {type.name}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <DropdownButton
                            alignRight
                            title="Sort"
                            variant="accent"
                            className="sort-menu default-menu"
                            onSelect={filterProducts}
                        >
                            {Object.keys(productFilters).map((type, index) => (
                                <Dropdown.Item
                                    key={index}
                                    eventKey={productFilters[type]}
                                >
                                    {productFilters[type]}
                                </Dropdown.Item>
                            ))}
                        </DropdownButton>
                    </div>
                    {/* Sorting Block End */}

                    {loading && page == 1 ? <Loader /> : (
                        <InfiniteScroll 
                            dataLength={products.length} 
                            next={() => {
                                const nextPage = page + 1;
                                setPage(nextPage);
                                getUserSavedProducts(selectedProductFilter, nextPage, searchTerm, sortOption);
                            }} 
                            hasMore={hasMore}
                            loader={loading ? <Loader /> : null}
                            style={{overflow: 'visible'}}
                        >
                            {/* saved product loop */}
                            <div className="product-list-item-wrapper">
                                {status ? (
                                    status
                                ) : products.length > 0 ? (
                                    products.map((product) => (
                                        <SavedProduct
                                            key={product.id}
                                            {...product}
                                            deleteItem={() =>
                                                handleDeleteProduct(product.id)
                                            }
                                            moveItem={() =>
                                                handleMoveProduct(product.id)
                                            }
                                            loadItem={() =>
                                              handleLoadItem(product.product_id)
                                            }
                                        />
                                    ))
                                ) : (
                                    generalMessages.EMPTY_PRODUCTS
                                )}
                            </div>
                        </InfiniteScroll>
                    )}
                </div>
            </section>
        </div>
    );
};

export default SavedProducts;
